import React, { useState, useEffect } from 'react';
import styles from '../styles/EventsPage.module.css';

// Define the types for each event category
interface BaseEvent {
  id: number;
  title: string;
  date: string;
  description: string;
}

interface StandardEvent extends BaseEvent {
  time: string;
  location: string;
  status: string;
}

interface AnnouncementEvent extends BaseEvent {
  category: string;
}

// Define a type for the entire events object
interface EventsData {
  conferences: StandardEvent[];
  workshops: StandardEvent[];
  fairs: StandardEvent[];
  announcements: AnnouncementEvent[];
}

// Data for the new sidebar widget
interface FeaturedEvent {
  id: number;
  title: string;
  date: string;
  category: 'Conference' | 'Workshop' | 'Fair';
}

const EventsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'conferences' | 'workshops' | 'fairs' | 'announcements'>('conferences');
  
  // A new state to manage which featured events to show in the sidebar
  const [featuredEvents, setFeaturedEvents] = useState<FeaturedEvent[]>([]);

  // Event data with explicit type
  const events: EventsData = {
    conferences: [
      {
        id: 1,
        title: 'Annual Philosophy Conference',
        date: '2025-10-15',
        time: '9:00 AM - 5:00 PM',
        location: 'University Auditorium',
        description:
          'Join us for our flagship event featuring renowned philosophers discussing contemporary ethical dilemmas in technology.',
        status: 'Upcoming',
      },
      {
        id: 2,
        title: 'International Ethics Symposium',
        date: '2025-11-20',
        time: '10:00 AM - 4:00 PM',
        location: 'Philosophy Hall, Room 101',
        description:
          'Global experts explore the future of ethics in an increasingly digital world.',
        status: 'Registration Open',
      },
      {
        id: 3,
        title: 'History of Philosophy Colloquium',
        date: '2025-09-30',
        time: '2:00 PM - 6:00 PM',
        location: 'Library Conference Center',
        description:
          'Deep dive into the evolution of philosophical thought from ancient to modern times.',
        status: 'Upcoming',
      },
    ],
    workshops: [
      {
        id: 1,
        title: 'Logic and Critical Thinking Workshop',
        date: '2025-10-05',
        time: '3:00 PM - 5:00 PM',
        location: 'Philosophy Hall, Room 205',
        description:
          'Develop essential reasoning skills through practical exercises and group discussions.',
        status: 'Open',
      },
      {
        id: 2,
        title: 'Academic Writing for Philosophers',
        date: '2025-11-12',
        time: '1:00 PM - 4:00 PM',
        location: 'Writing Center',
        description:
          'Master the art of philosophical writing with guidance from published faculty members.',
        status: 'Registration Open',
      },
      {
        id: 3,
        title: 'Ethics Bowl Training Session',
        date: '2025-09-25',
        time: '4:00 PM - 6:00 PM',
        location: 'Debate Hall',
        description:
          'Prepare for the regional ethics competition with our coaching team.',
        status: 'Limited Spots',
      },
    ],
    fairs: [
      {
        id: 1,
        title: 'Graduate School Fair',
        date: '2025-10-18',
        time: '11:00 AM - 2:00 PM',
        location: 'Student Union Building',
        description:
          'Meet representatives from top philosophy graduate programs across the country.',
        status: 'Upcoming',
      },
      {
        id: 2,
        title: 'Research Opportunities Fair',
        date: '2025-11-05',
        time: '10:00 AM - 1:00 PM',
        location: 'Research Commons',
        description:
          'Discover research positions and projects within the philosophy department.',
        status: 'Open',
      },
    ],
    announcements: [
      {
        id: 1,
        title: 'New Philosophy Journal Launch',
        date: '2025-09-15',
        description:
          "The department is proud to announce the launch of 'Contemporary Philosophical Thought', our new peer-reviewed journal.",
        category: 'Department News',
      },
      {
        id: 2,
        title: 'Call for Papers: Ethics in AI',
        date: '2025-10-01',
        description:
          'Submit your papers for the special issue on Artificial Intelligence and Moral Philosophy. Deadline: December 15, 2025.',
        category: 'Research',
      },
      {
        id: 3,
        title: 'Visiting Scholar Program Applications Open',
        date: '2025-09-18',
        description:
          'Apply for our visiting scholar program for Spring 2026. Deadline: October 30, 2025.',
        category: 'Opportunities',
      },
      {
        id: 4,
        title: 'Department Office Hours Update',
        date: '2025-09-20',
        description:
          'During midterms, additional office hours will be available. Check the department portal for schedules.',
        category: 'Department News',
      },
    ],
  };

  useEffect(() => {
    const allStandardEvents = [
      ...events.conferences,
      ...events.workshops,
      ...events.fairs
    ];

    const upcomingEvents: FeaturedEvent[] = allStandardEvents
      .filter(event => new Date(event.date) >= new Date())
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .slice(0, 3)
      .map(event => {
        let category: 'Conference' | 'Workshop' | 'Fair';
        // Determine the specific category based on which original array the event belongs to
        if (events.conferences.some(c => c.id === event.id && c.title === event.title)) {
          category = 'Conference';
        } else if (events.workshops.some(w => w.id === event.id && w.title === event.title)) {
          category = 'Workshop';
        } else {
          category = 'Fair';
        }

        return {
          id: event.id,
          title: event.title,
          date: event.date,
          category: category
        };
      });

    setFeaturedEvents(upcomingEvents);
  }, [events]);

  // WhatsApp link for the coordinator
  const coordinatorWhatsappLink = "https://wa.me/+2348012345678"; // Replace with the actual WhatsApp number

  return (
    <div className={styles['philo-events-container']}>
      <header className={styles['philo-events-header']}>
        <div className={styles['philo-header-overlay']}>
          <h1>Events & Announcements</h1>
          <p>Stay updated with the latest happenings in our department</p>
        </div>
      </header>

      <div className={styles['philo-events-content']}>
        <div className={styles['philo-events-tabs']}>
          <button
            className={`${styles['philo-tab']} ${activeTab === 'conferences' ? styles['philo-active'] : ''}`}
            onClick={() => setActiveTab('conferences')}
          >
            Conferences
          </button>
          <button
            className={`${styles['philo-tab']} ${activeTab === 'workshops' ? styles['philo-active'] : ''}`}
            onClick={() => setActiveTab('workshops')}
          >
            Workshops
          </button>
          <button
            className={`${styles['philo-tab']} ${activeTab === 'fairs' ? styles['philo-active'] : ''}`}
            onClick={() => setActiveTab('fairs')}
          >
            Reference Fairs
          </button>
          <button
            className={`${styles['philo-tab']} ${activeTab === 'announcements' ? styles['philo-active'] : ''}`}
            onClick={() => setActiveTab('announcements')}
          >
            Announcements
          </button>
        </div>

        <div className={styles['philo-events-main']}>
          <div className={styles['philo-events-list']}>
            <h2 className={styles['philo-section-title']}>
              {activeTab === 'conferences' && 'Upcoming Conferences'}
              {activeTab === 'workshops' && 'Workshops'}
              {activeTab === 'fairs' && 'Reference Fairs'}
              {activeTab === 'announcements' && 'Latest Announcements'}
            </h2>

            <div className={styles['philo-events-grid']}>
              {events[activeTab].map(event => (
                <div key={event.id} className={styles['philo-event-card']}>
                  <div className={styles['philo-event-header']}>
                    <h3>{event.title}</h3>
                    {'status' in event && (
                      <span
                        className={`${styles['philo-event-status']} ${
                          styles[`philo-status-${(event as StandardEvent).status.toLowerCase().replace(/ /g, '-')}`]
                        }`}
                      >
                        {(event as StandardEvent).status}
                      </span>
                    )}
                  </div>

                  <div className={styles['philo-event-details']}>
                    <div className={styles['philo-event-date']}>
                      <i className={styles['philo-icon-calendar']}></i>
                      <span>
                        {event.date}
                        {'time' in event && ` • ${(event as StandardEvent).time}`}
                      </span>
                    </div>

                    {'location' in event && (
                      <div className={styles['philo-event-location']}>
                        <i className={styles['philo-icon-location']}></i>
                        <span>{(event as StandardEvent).location}</span>
                      </div>
                    )}

                    {'category' in event && (
                      <div className={styles['philo-event-category']}>
                        <i className={styles['philo-icon-category']}></i>
                        <span>{(event as AnnouncementEvent).category}</span>
                      </div>
                    )}
                  </div>

                  <p className={styles['philo-event-description']}>{event.description}</p>

                  <div className={styles['philo-event-actions']}>
                    {/* Conditional button based on activeTab */}
                    {activeTab === 'announcements' ? (
                      <a 
                        href={coordinatorWhatsappLink} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={styles['philo-contact-button']}
                      >
                        Contact Manager
                      </a>
                    ) : (
                      <a 
                        href={coordinatorWhatsappLink} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className={styles['philo-contact-button']}
                      >
                        Contact Coordinator
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <aside className={styles['philo-events-sidebar']}>
            {/* Featured Events section */}
            <div className={styles['philo-sidebar-card']}>
              <h3>Featured Events</h3>
              <ul className={styles['philo-featured-events-list']}>
                {featuredEvents.length > 0 ? (
                  featuredEvents.map(event => (
                    <li key={event.id}>
                      <div className={styles['philo-featured-event-date']}>
                        <div className={styles['philo-date-day']}>{new Date(event.date).getDate()}</div>
                        <div className={styles['philo-date-month']}>{new Date(event.date).toLocaleString('default', { month: 'short' })}</div>
                      </div>
                      <div className={styles['philo-featured-event-details']}>
                        <div className={styles['philo-featured-event-title']}>{event.title}</div>
                        <div className={styles['philo-featured-event-category']}>{event.category}</div>
                      </div>
                    </li>
                  ))
                ) : (
                  <p>No featured events at this time.</p>
                )}
              </ul>
            </div>

            {/* <div className={styles['philo-sidebar-card']}>
              <h3>Submit an Event</h3>
              <p>Have an event you'd like to share with the department?</p>
              <button className={styles['philo-submit-event']}>Submit Event</button>
            </div> */}

            <div className={styles['philo-sidebar-card']}>
              <h3>Upcoming Deadlines</h3>
              <ul className={styles['philo-deadlines-list']}>
                <li>
                  <div className={styles['philo-deadline-date']}>Oct 15, 2025</div>
                  <div className={styles['philo-deadline-title']}>Conference Abstract Submissions</div>
                </li>
                <li>
                  <div className={styles['philo-deadline-date']}>Nov 1, 2025</div>
                  <div className={styles['philo-deadline-title']}>Graduate Applications</div>
                </li>
                <li>
                  <div className={styles['philo-deadline-date']}>Dec 15, 2025</div>
                  <div className={styles['philo-deadline-title']}>Journal Paper Submissions</div>
                </li>
                <li>
                  <div className={styles['philo-deadline-date']}>Jan 10, 2026</div>
                  <div className={styles['philo-deadline-title']}>Study Abroad Applications</div>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default EventsPage;